/* @jsx h */
import {
  TransitionGroup,
  Teleport,
  defineComponent,
  h,
  inject,
  onBeforeUnmount,
  provide,
  ref,
} from "vue";
import { nanoid } from "nanoid";

export type ToastType = "info" | "success" | "warning" | "error";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  icon?: string;
  title?: string;
}

export interface ToastContextType {
  showToast: (
    message: string,
    options?: {
      type?: ToastType;
      autoHide?: number;
      icon?: string;
      title?: string;
    },
  ) => void;
}

const ToastContextKey = Symbol("ToastContext");
const DEFAULT_AUTO_HIDE_MS = 7000;
const TOAST_TRANSITION_NAME = "app-toast";

export const ToastProvider = defineComponent({
  name: "ToastProvider",
  setup(_, { slots }) {
    const toasts = ref<Toast[]>([]);
    const timers = new Map<string, number>();

    const clearToastTimer = (id: string) => {
      const timer = timers.get(id);
      if (timer === undefined) {
        return;
      }

      window.clearTimeout(timer);
      timers.delete(id);
    };

    const removeToast = (id: string) => {
      clearToastTimer(id);
      toasts.value = toasts.value.filter((toast) => toast.id !== id);
    };

    const showToast = (
      message: string,
      {
        type = "info",
        autoHide = DEFAULT_AUTO_HIDE_MS,
        icon,
        title,
      }: {
        type?: ToastType;
        autoHide?: number;
        icon?: string;
        title?: string;
      } = {},
    ) => {
      const id = nanoid();

      toasts.value.push({
        id,
        message,
        type,
        icon,
        title,
      });

      if (autoHide > 0) {
        const timer = window.setTimeout(() => {
          removeToast(id);
        }, autoHide);

        timers.set(id, timer);
      }
    };

    onBeforeUnmount(() => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    });

    provide(ToastContextKey, { showToast });

    return () => (
      <>
        {slots.default?.()}
        <Teleport to="body">
          <style>{toastStyles}</style>
          <div class="app-toast-stack pointer-events-none fixed z-[10000000] p-4">
            <div class="flex w-full flex-col md:w-auto md:items-end">
              <div class="app-toast-list flex w-full flex-col md:w-auto md:items-end">
                <TransitionGroup name={TOAST_TRANSITION_NAME} tag="div">
                  {toasts.value.map((toast) => (
                    <div
                      key={toast.id}
                      data-toast-id={toast.id}
                      class={`pointer-events-auto alert l-box-shadow flex w-full items-start justify-start border-2 bg-base-200 text-accent shadow-xl glossy-bg md:min-w-80 md:max-w-md md:w-auto border-${getAlertClass(
                        toast.type,
                      )} alert-${getAlertClass(toast.type)}`}
                    >
                      <span
                        class={`material-symbols-outlined text-${toast.type} self-start text-5xl`}
                      >
                        {toast.icon || iconMap[toast.type] || ""}
                      </span>
                      <div class="min-w-0 flex-1">
                        <h3 class="text-left font-bold">
                          {toast.title ||
                            toast.type.charAt(0).toUpperCase() +
                              toast.type.slice(1)}
                        </h3>
                        <div class="text-md whitespace-normal text-left">
                          {toast.message}
                        </div>
                      </div>
                      <button
                        class="btn btn-circle btn-xs self-start"
                        onClick={() => removeToast(toast.id)}
                      >
                        <span class="material-symbols-outlined text-sm">
                          close
                        </span>
                      </button>
                    </div>
                  ))}
                </TransitionGroup>
              </div>
            </div>
          </div>
        </Teleport>
      </>
    );
  },
});

const toastStyles = `
.app-toast-stack {
  left: 0;
  right: 0;
  top: var(--safe-area-top, 0px);
}

.app-toast-list > div {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
}

.app-toast-enter-active,
.app-toast-leave-active,
.app-toast-move {
  transition:
    transform 220ms ease,
    opacity 220ms ease;
}

.app-toast-enter-from,
.app-toast-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}

.app-toast-leave-active {
  position: relative;
}

@media (min-width: 768px) {
  .app-toast-stack {
    left: auto;
    right: 0;
    top: auto;
    bottom: 0;
    width: auto;
  }

  .app-toast-list > div {
    align-items: flex-end;
    gap: 1rem;
    width: auto;
  }

  .app-toast-enter-from,
  .app-toast-leave-to {
    transform: translateX(16px);
  }
}
`;

const iconMap: Record<string, string> = {
  error: "error",
  warning: "warning",
  success: "check_box",
  info: "info_i",
};

function getAlertClass(type: ToastType): string {
  switch (type) {
    case "success":
      return "success";
    case "warning":
      return "warning";
    case "error":
      return "error";
    case "info":
    default:
      return "info";
  }
}

export function useToast(): ToastContextType {
  const context = inject<ToastContextType>(ToastContextKey);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
